import {useState, useCallback, useEffect, useMemo} from 'react';
import produce from 'immer';
import {useTranslation} from 'react-i18next';
import {useSMCSnackbar} from '../../../utils/hooks';
import {setProperty, getErrorCode, isStringI18n, extractI18nString} from '../../../utils/utils';
import Api from '../../../api/SMPFetchHelper';
import {ObjectTypes, ApiErrorCodes, ErrorCodes} from '../../../api/consts';
import {SessionDataGetSchema} from '../../../api/DBSchema/SessionData';
import {useSMCErrorHandler} from '../../../utils/errorUtils';
import store from '../../../store';
import {push} from 'connected-react-router';
import {SMCPaths, helpContextPageUrl} from '../../../conf/Routes';
import {SystemUserGetSchema} from '../../../api/DBSchema/SystemUser';
import SystemUserGeneral from './InnerPages/SystemUserGeneral';
import SystemUserExpiration from './InnerPages/SystemUserExpiration';
import generalIcon from '../../../assets/images/general.png';
import hourglassIcon from '../../../assets/images/hourglass.png';
import megaphoneIcon from '../../../assets/images/megaphone.png';
import padlockIcon from '../../../assets/images/padlock.png';
import pageIcon from '../../../assets/images/page.png';
import SystemUserContact from './InnerPages/SystemUserContact';
import SystemUserTwoStepAuth from './InnerPages/SystemUserTwoStepAuth';
import SystemUserStatus from './InnerPages/SystemUserStatus';
import SystemUserDomains from './InnerPages/SystemUserDomains';


export function useSystemUserEditor(systemUserId, defaultChosenPageId) {
    const { t } = useTranslation();
    const { enqueueErrorSnackbar, enqueueLogoutSnackbar, enqueueSuccessSnackbar } = useSMCSnackbar();
    const { getErrorMessage, getMessagesArrayByPages } = useSMCErrorHandler();

    const [origSystemUserObject, setOrigSystemUserObject] = useState({});
    const [systemUserObject, setSystemUserObject] = useState({});

    // System user uses system roles, get all available system roles that he may use
    const [availableSystemRoles, setAvailableSystemRoles] = useState([]);
    const [minimumPasswordLength, setMinimumPasswordLength] = useState();
    const [maximumPasswordLength, setMaximumPasswordLength] = useState();

    const [isPageDataLoaded, setIsPageDataLoaded] = useState(false);
    const [isInnerPageLoading, setIsInnerPageLoading] = useState(false);
    const [isPageLoadFailed, setIsPageLoadFailed] = useState(false);
    const [errorMessagesByPageList, setErrorMessagesByPageList] = useState(null);

    const [isObjectDirty, setIsObjectDirty] = useState(false);

    const [chosenPageId, setChosenPageId] = useState(defaultChosenPageId || 'general');

    const editorPages = useMemo(() => ([
        { id: SystemUserGetSchema.pages.general.pageId,        title: t('SMP.GENERAL'),                                 icon: generalIcon,   pageElement: SystemUserGeneral,     helpId: helpContextPageUrl.systemUserGeneral },
        { id: SystemUserGetSchema.pages.expiration.pageId,     title: t('SMP.USERS.TABLE.INNER_MENU.EXPIRATION'),       icon: hourglassIcon, pageElement: SystemUserExpiration,  helpId: helpContextPageUrl.systemUserExpiration },
        { id: SystemUserGetSchema.pages.contact.pageId,        title: t('SMP.USERS.TABLE.INNER_MENU.CONTACT'),          icon: megaphoneIcon, pageElement: SystemUserContact,     helpId: helpContextPageUrl.systemUserContact },
        { id: SystemUserGetSchema.pages.twoStepAuth.pageId,    title: t('SMP.TREE_NODE_INNER.TWO_STEP_AUTHENTICATION'), icon: padlockIcon,   pageElement: SystemUserTwoStepAuth, helpId: helpContextPageUrl.showingUsers },
        { id: SystemUserGetSchema.pages.status.pageId,         title: t('SMP.USERS.TABLE.INNER_MENU.STATUS'),             pageElement: SystemUserStatus,      helpId: helpContextPageUrl.systemUserStatus },
        { id: SystemUserGetSchema.pages.serviceDomains.pageId, title: t('SMP.SYSTEM_USERS.INNER_MENU.SERVICE_DOMAINS'), icon: pageIcon,      pageElement: SystemUserDomains,     helpId: helpContextPageUrl.systemUserDomains }
    ]), [t]);

    const [pageIdToPageName, pageIdToHelpId] = useMemo(() => {
        const _pageIdToPageName = {};
        const _pageIdToHelpId = {};
        const addPagesData = (pagesRoot) => {
            pagesRoot.forEach(page => {
                if (page.id) {
                    _pageIdToPageName[page.id] = page.title;
                    _pageIdToHelpId[page.id] = page.helpId;
                }
                if (page.children) {
                    addPagesData(page.children);
                }
            })
        }
        addPagesData(editorPages);
        return [_pageIdToPageName, _pageIdToHelpId];
    }, [editorPages]);

    const helpId = useMemo(() => pageIdToHelpId[chosenPageId], [pageIdToHelpId, chosenPageId]);

    const fetchSystemUser = useCallback(async () => {
        setIsPageDataLoaded(false);
        try {
            const [fetchSystemUserResp, getSessionDataResp] = await Promise.all([
                Api.fetchSingleObject(ObjectTypes.SYSTEM_USER, systemUserId),
                Api.getSessionData({attributes: [
                    SessionDataGetSchema.availableSystemRoles,
                    SessionDataGetSchema.minimumPasswordLength,
                    SessionDataGetSchema.maximumPasswordLength
                ]})
            ]);
            const systemUser = fetchSystemUserResp.data.data.user;
            const availableSystemRoles = getSessionDataResp.data.availableSystemRoles;
            const minimumPasswordLength = getSessionDataResp.data[SessionDataGetSchema.minimumPasswordLength];
            const maximumPasswordLength = getSessionDataResp.data[SessionDataGetSchema.maximumPasswordLength];
            // Validate server response
            if (!systemUser || !availableSystemRoles) {
                console.error('Get system user response failed:');
                !systemUser && console.error('system user response returned empty');
                !availableSystemRoles && console.error('getSessionData returned without availableSystemRoles');
                throw new Error(ErrorCodes.SERVER_ERROR);
            }
            setOrigSystemUserObject(systemUser);
            setSystemUserObject(systemUser);
            setAvailableSystemRoles(availableSystemRoles);
            setMinimumPasswordLength(minimumPasswordLength);
            setMaximumPasswordLength(maximumPasswordLength);
            setIsPageLoadFailed(false);
            setIsObjectDirty(false); // Just fetched, not dirty
        } catch (e) {
            const errorCode = getErrorCode(e) || ApiErrorCodes.GENERAL_ERROR;
            if (errorCode === ApiErrorCodes.INVALID_SESSION_ERROR) {
                enqueueLogoutSnackbar(t(errorCode));
            } else {
                enqueueErrorSnackbar(t(errorCode));
            }
            setIsPageLoadFailed(true);
        }
        setIsPageDataLoaded(true);
    }, [systemUserId, enqueueErrorSnackbar, enqueueLogoutSnackbar, t]);

    const setSystemUserData = useCallback((paramPath, newValue) => {
        setIsObjectDirty(true); // Data changed, object is dirty
        setSystemUserObject(systemUserObj => produce(systemUserObj, draft => {
            setProperty(draft, paramPath, newValue);
        }));
    }, []);

    const verifyData = useCallback(() => {
        setIsInnerPageLoading(true);
        Api.verifySingleObject(ObjectTypes.SYSTEM_USER, systemUserId, systemUserObject)
        .then(() => {
            // On no error, clear error message
            setErrorMessagesByPageList(null);
        })
        .catch((error) => {
            const errorMessageArrayByPage = getMessagesArrayByPages(error, pageIdToPageName);
            if (errorMessageArrayByPage) {
                setErrorMessagesByPageList(errorMessageArrayByPage);
            }
        })
        .finally(() => {
            setIsInnerPageLoading(false);
        });
    }, [systemUserId, getMessagesArrayByPages, systemUserObject, pageIdToPageName]);

    const onPageChange = useCallback((pageId) => {
        verifyData();
        setChosenPageId(pageId);
    }, [verifyData]);

    const saveButtonOnClick = useCallback(() => {
        Api.updateSingleObject(ObjectTypes.SYSTEM_USER, systemUserId, systemUserObject)
        .then(() => {
            setIsPageDataLoaded(false);
            setErrorMessagesByPageList(null);
            fetchSystemUser();
            enqueueSuccessSnackbar(t('OBJECT_EDITOR_OBJ_SAVED_OK'));
        })
        .catch((error) => {
            const errorMessage = getErrorMessage(error);
            alert(errorMessage);
        })
        .finally(() => {
            setIsPageDataLoaded(true);
        });
    }, [systemUserId, getErrorMessage, systemUserObject, fetchSystemUser, enqueueSuccessSnackbar, t]);

    const deleteButtonOnClick = useCallback(() => {
        if (window.confirm(t('OBJECT_EDITOR_OBJ_DELETE', [systemUserId] ))) {
            Api.deleteObjects(ObjectTypes.SYSTEM_USER, [systemUserId])
            .then(res => {
                const deleteResult = res.data.results[0];
                if (deleteResult.success) {
                    setIsObjectDirty(false); // Set as false to not trigger dirty modal
                    store.dispatch(push(SMCPaths.systemUsers));
                    enqueueSuccessSnackbar(t('OBJECT_EDITOR_OBJ_DELETE_OK', [systemUserId]));
                } else {
                    const deleteErrorCode = deleteResult.errorCode;
                    let deleteErrorParams;
                    if (deleteResult.errorParams) {
                        deleteErrorParams = deleteResult.errorParams.map(param => (
                            isStringI18n(param)) ? t(extractI18nString(param)) : param
                        );
                    }
                    enqueueErrorSnackbar(t(extractI18nString(deleteErrorCode), deleteErrorParams));
                }
            })
            .catch(err => {
                const errorCode = getErrorMessage(err);
                enqueueErrorSnackbar(t(errorCode));
            });
        }
    }, [systemUserId, getErrorMessage, enqueueSuccessSnackbar, enqueueErrorSnackbar, t]);

    const revertButtonOnClick = useCallback(() => {
        setSystemUserObject(origSystemUserObject);
        setIsObjectDirty(false); // Is not dirty, object is reset to originl values
        setErrorMessagesByPageList(null);
    }, [origSystemUserObject]);

    // Fetch system user and roles on loading of page
    useEffect(() => {
        fetchSystemUser();
    }, [fetchSystemUser]);
    
    return {
        systemUserObject,
        isObjectDirty,
        editorPages,
        helpId,
        origSystemUserObject,
        isPageDataLoaded,
        isInnerPageLoading,
        isPageLoadFailed,
        chosenPageId,
        errorMessagesByPageList,
        onPageChange,
        setSystemUserData,
        saveButtonOnClick,
        deleteButtonOnClick,
        revertButtonOnClick,
        reloadPageData: fetchSystemUser,
        availableSystemRoles,
        minimumPasswordLength,
        maximumPasswordLength
    };
}